
import React from "react";
import { Edit, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { CalendarViewProps } from "../types";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/components/LanguageContext";

export const DayView = ({
  settings,
  date,
  timeSlots,
  events,
  handleDateClick,
  handleEventClick,
  handleDeleteEvent,
  getEventColor
}: CalendarViewProps) => {
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  
  if (!timeSlots.length || !settings) return null;

  // Use the correct locale based on language
  const dateLocale = language === 'pt' ? pt : undefined;

  return (
    <div className="grid md:grid-cols-2 gap-4 mt-4">
      <div className="space-y-4 w-full overflow-hidden">
        <div className="text-center pb-4 border-b border-gray-300 dark:border-gray-700">
          <div className="font-medium dark:text-gray-200">
            {format(date, "eeee", { locale: dateLocale })}
          </div>
          <div className="text-sm text-muted-foreground dark:text-gray-400">
            {format(date, language === 'pt' ? "d 'de' MMMM" : "MMMM d", { locale: dateLocale })}
          </div>
        </div>

        <div className="relative h-[600px] overflow-y-auto">
          {timeSlots.map((time) => (
            <div
              key={time}
              className="absolute left-12 right-0 border-t border-gray-300 dark:border-gray-700 hover:bg-muted/50 dark:hover:bg-gray-700/30 cursor-pointer"
              style={{
                top: `${(Number(time.split(":")[0]) - Number(settings.work_start_time.split(":")[0])) * 64}px`,
                height: "64px"
              }}
              onClick={() => {
                const [hours, minutes] = time.split(":").map(Number);
                const clickedDate = new Date(date);
                clickedDate.setHours(hours, minutes);
                handleDateClick(clickedDate);
              }}
            >
              <span className="absolute -left-12 text-sm text-muted-foreground dark:text-gray-400">
                {time}
              </span>
            </div>
          ))}

          {events.map((event) => {
            const startTime = new Date(event.start_time);
            const endTime = new Date(event.end_time);
            const workStartHour = parseInt(settings.work_start_time.split(':')[0]);
            const minutesSinceStart = (startTime.getHours() - workStartHour) * 60 + startTime.getMinutes();
            const duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
            
            const top = (minutesSinceStart * 64) / 60;
            const height = (duration * 64) / 60;

            const studentName = event.profile?.first_name && event.profile?.last_name 
              ? `${event.profile.first_name} ${event.profile.last_name}` 
              : null;
            
            const displayTitle = event.event_type === 'other' 
              ? event.title 
              : (studentName || event.title);

            return (
              <div
                key={event.id}
                className={`absolute left-12 right-2 p-2 rounded-lg ${getEventColor(event.event_type, event.status)} shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-md`}
                style={{
                  top: `${top}px`,
                  height: `${height}px`,
                  minHeight: "20px",
                }}
                onClick={() => handleEventClick(event)}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {displayTitle}
                    </div>
                    <div className="text-xs truncate">
                      {format(startTime, "HH:mm")} - {format(endTime, "HH:mm")}
                    </div>
                    {studentName && event.event_type !== 'other' && (
                      <div className="text-xs opacity-75 truncate dark:text-gray-300">
                        {event.title}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event);
                      }}
                      className="p-1 hover:bg-white/20 dark:text-gray-300 dark:hover:text-white rounded"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEvent(event);
                      }}
                      className="p-1 hover:bg-white/20 dark:text-gray-300 dark:hover:text-white rounded"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
